/**
 * Button Utilities
 *
 * Utility functions for the Button component.
 * Exported separately to comply with React Fast Refresh requirements.
 *
 * @see Button.tsx for component implementation
 */

// ============================================================================
// CLASSNAME FILTERING - STRICT ENFORCEMENT
// ============================================================================

/**
 * Patterns for ALLOWED layout classes only.
 * All styling classes are BLOCKED.
 */
const ALLOWED_LAYOUT_PATTERNS: RegExp[] = [
  // Margins (including negative)
  /^-?m[trblxy]?-\d+$/,
  /^-?m[trblxy]?-auto$/,
  /^-?m[trblxy]?-px$/,
  // Positioning
  /^(relative|absolute|fixed|sticky|static)$/,
  /^(top|right|bottom|left|inset)-/,
  /^z-\d+$/,
  // Flex/grid item positioning (NOT flex container styling)
  /^flex-(1|auto|none|initial)$/,
  /^(self-|place-self-)/,
  /^(col-span-|row-span-)/,
  /^order-/,
  // Visibility (accessibility)
  /^sr-only$/,
  /^not-sr-only$/,
  // Responsive display/visibility utilities (e.g., lg:hidden, md:block, sm:hidden)
  /^(xs|sm|md|lg|xl|2xl)?:?(hidden|block|inline|inline-block|flex|inline-flex|grid|invisible|visible)$/,
];

/**
 * Patterns for BLOCKED styling classes.
 * These are filtered out to prevent design system bypasses.
 */
const BLOCKED_STYLING_PATTERNS: { pattern: RegExp; category: string }[] = [
  // Colors (bg, text, border colors)
  { pattern: /^bg-/, category: 'background' },
  { pattern: /^text-/, category: 'text color' },
  { pattern: /^border-/, category: 'border' },
  { pattern: /^ring-/, category: 'ring' },
  { pattern: /^shadow/, category: 'shadow' },
  { pattern: /^from-/, category: 'gradient' },
  { pattern: /^via-/, category: 'gradient' },
  { pattern: /^to-/, category: 'gradient' },
  // Typography
  { pattern: /^font-/, category: 'font' },
  { pattern: /^leading-/, category: 'line-height' },
  { pattern: /^tracking-/, category: 'letter-spacing' },
  // Border styling (not colors)
  { pattern: /^rounded/, category: 'border-radius' },
  // Sizing (managed by size prop)
  { pattern: /^[wh]-/, category: 'width/height' },
  { pattern: /^min-[wh]-/, category: 'min-size' },
  { pattern: /^max-[wh]-/, category: 'max-size' },
  { pattern: /^p[xytblr]?-/, category: 'padding' },
  // Flex/grid container (use Stack/Row/Grid components)
  { pattern: /^flex$/, category: 'flex-container' },
  { pattern: /^inline-flex$/, category: 'inline-flex' },
  { pattern: /^grid$/, category: 'grid' },
  { pattern: /^gap-/, category: 'gap' },
  { pattern: /^items-/, category: 'align-items' },
  { pattern: /^justify-/, category: 'justify-content' },
  { pattern: /^content-/, category: 'align-content' },
  // Effects
  { pattern: /^opacity-/, category: 'opacity' },
  { pattern: /^blur-/, category: 'blur' },
  { pattern: /^brightness-/, category: 'brightness' },
];

/**
 * Check if a class matches any blocked pattern.
 */
function isBlockedClass(cls: string): { blocked: boolean; category: string } {
  for (const { pattern, category } of BLOCKED_STYLING_PATTERNS) {
    if (pattern.test(cls)) {
      return { blocked: true, category };
    }
  }
  return { blocked: false, category: '' };
}

/**
 * Check if a class matches any allowed pattern.
 */
function isAllowedClass(cls: string): boolean {
  return ALLOWED_LAYOUT_PATTERNS.some((pattern) => pattern.test(cls));
}

/**
 * Filters className to only allow layout classes.
 * Styling classes are removed to enforce design tokens.
 *
 * @param className - The className string to filter
 * @returns Filtered className with only layout classes
 */
export function filterLayoutClasses(className: string | undefined): string {
  if (!className) return '';

  const classes = className.split(/\s+/).filter(Boolean);
  const allowed: string[] = [];
  const blocked: { cls: string; category: string }[] = [];

  for (const cls of classes) {
    const blockResult = isBlockedClass(cls);

    if (blockResult.blocked) {
      blocked.push({ cls, category: blockResult.category });
    } else if (isAllowedClass(cls)) {
      allowed.push(cls);
    }
    // Classes not matching either pattern are silently dropped
  }

  // Development warning for blocked classes
  if (import.meta.env.DEV && blocked.length > 0) {
    // Blocked classes detected but warning removed (cleaned up console logs)
  }

  return allowed.join(' ');
}
